import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { ModalService } from '@cfp/core';
import { Button, SidePanel, Table, TableColumn } from '@cfp/ui';
import { AlertModalResult, AlertService } from '@cfp/ui/feedback';
import { TranslateModule } from '@ngx-translate/core';
import { PortfolioTagListItemDTO, TagsService } from 'csr-asset-app';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TagsAddModal } from '../tags-add-modal';
import { TagsToolbar } from '../tags-toolbar';

@Component({
  selector: 'cfp-tags-portfolio-modal',
  templateUrl: './tags-portfolio-modal.html',
  imports: [TagsToolbar, Button, Table, SidePanel, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TagsPortfolioModal implements OnInit {
  public portfolioId: number;

  protected readonly columns: TableColumn[] = [
    {
      key: 'Tag',
      name: 'tags.manage.tag',
      width: '75%'
    },
    {
      key: 'Used on',
      name: 'tags.manage.usedOn',
      width: '20%'
    },
    {
      key: 'Action',
      name: 'tags.manage.action',
      width: '5%'
    }
  ];

  protected readonly searchText = signal<string | undefined>(undefined);
  private readonly tags = signal<PortfolioTagListItemDTO[] | undefined>(undefined);

  protected readonly filteredTags = computed(() => {
    const tags = this.tags();
    const searchText = this.searchText()?.toLowerCase();
    return searchText && tags ? tags.filter((tag) => tag.tag.toLowerCase().includes(searchText)) : tags;
  });

  private readonly tagsService = inject(TagsService);
  private readonly bsModalRef = inject(BsModalRef);
  private readonly modalService = inject(ModalService);
  private readonly alertService = inject(AlertService);

  public ngOnInit(): void {
    this.fetchTags();
  }

  protected fetchTags(): void {
    this.tagsService.v2PortfoliosPortfolioIdTagsGet(this.portfolioId).subscribe((tags) => {
      this.tags.set(tags.sort((a, b) => a.tag.localeCompare(b.tag)));
    });
  }

  protected close(): void {
    this.bsModalRef.hide();
  }

  protected search(searchText: string | undefined): void {
    this.searchText.set(searchText);
  }

  protected openAddTagModal() {
    const bsModalRef = this.modalService.show(TagsAddModal, {
      initialState: {
        portfolioId: this.portfolioId
      }
    });

    if (bsModalRef.content) {
      bsModalRef.content.closeClicked.subscribe(() => {
        this.fetchTags();
      });
    }
  }

  protected deleteTag(tag: PortfolioTagListItemDTO) {
    this.alertService.showConfirmation('warning', 'tags.delete', 'tags.delete.confirm').subscribe({
      next: (result) => {
        if (result === AlertModalResult.CONFIRM) {
          this.tagsService.v2PortfoliosPortfolioIdTagsTagIdDelete(this.portfolioId, tag.id).subscribe({
            next: () => {
              this.fetchTags();
            },
            error: () => {
              this.alertService.showAlert('error', 'error.general');
            }
          });
        }
      }
    });
  }
}
